<template>
  <section
    v-if="tagOptions.length > 0"
    class="wizard-section-card customer-tag-chips"
  >
    <div class="wizard-section-header">
      <v-icon
        small
        class="mr-2"
      >
        {{ icons.mdiPound }}
      </v-icon>
      <span class="wizard-section-title">顧客タグ</span>
      <v-spacer />
      <span
        v-if="selectedCount > 0"
        class="text-caption text--secondary"
      >
        {{ selectedCount }}件選択中
      </span>
    </div>

    <div class="tag-chip-grid">
      <button
        v-for="tag in tagOptions"
        :key="`tag-chip-${tag.id}`"
        type="button"
        class="tag-chip"
        :class="{ 'tag-chip--selected': isSelected(tag) }"
        @click="toggle(tag)"
      >
        <v-icon
          v-if="isSelected(tag)"
          small
          class="tag-chip-check mr-1"
        >
          {{ icons.mdiCheck }}
        </v-icon>
        <span>{{ tag.name }}</span>
      </button>
    </div>
  </section>
</template>

<script>
import { computed, inject, isRef } from '@vue/composition-api'
import { mdiPound, mdiCheck } from '@mdi/js'

export default {
  name: 'CustomerTagChips',
  props: {
    // formState のフラット形式 [{ id, customerTagId, tableId, _destroy, attributes }]
    value: { type: Array, default: () => [] },
  },
  emits: ['input'],
  setup(props, { emit }) {
    // タグマスタはレイアウト層 (LayoutWaiter 等) が provide する ref([{ id, type, attributes }])
    const injectedTags = inject('customerTags', [])

    const tagOptions = computed(() => {
      const list = isRef(injectedTags) ? injectedTags.value : injectedTags

      return (list || []).map(t => ({
        id: Number(t.id),
        name: t.attributes?.name || `タグ#${t.id}`,
        attributes: t.attributes || {},
      }))
    })

    const selectedIds = computed(() => new Set(
      (props.value || [])
        .filter(t => !t._destroy)
        .map(t => Number(t.customerTagId)),
    ))

    const isSelected = tag => selectedIds.value.has(tag.id)
    const selectedCount = computed(() => selectedIds.value.size)

    // 出力は CustomerTableTagging の updated と同じフラット形式に揃え、
    // useTableCreatePayload 以降の経路を変えない
    const toggle = tag => {
      if (isSelected(tag)) {
        emit('input', (props.value || [])
          .filter(t => Number(t.customerTagId) !== tag.id))
      } else {
        emit('input', [
          ...(props.value || []),
          {
            id: null,
            customerTagId: tag.id,
            tableId: null,
            _destroy: false,
            attributes: { ...tag.attributes },
          },
        ])
      }
    }

    return {
      tagOptions,
      isSelected,
      selectedCount,
      toggle,
      icons: { mdiPound, mdiCheck },
    }
  },
}
</script>

<style scoped>
.tag-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px;
  /* タグが多い店舗はチップ約3行分で内部スクロール */
  max-height: 216px;
  overflow-y: auto;
}
.tag-chip {
  display: inline-flex;
  align-items: center;
  /* タブレットのタップターゲット目安 44px */
  min-height: 44px;
  padding: 0 18px;
  border-radius: 22px;
  border: 1px solid rgba(127, 127, 127, 0.4);
  background: transparent;
  font-size: 0.95rem;
  font-weight: 500;
  color: inherit;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.tag-chip--selected {
  background: var(--v-primary-base);
  border-color: var(--v-primary-base);
  color: #fff;
  font-weight: 700;
}
.tag-chip--selected .v-icon {
  color: inherit !important;
}
.theme--light .tag-chip:hover:not(.tag-chip--selected) {
  background: rgba(0, 0, 0, 0.04);
}

/* ダークモード: primary 紫が背景に潰れるため白系で代替 (nomination-tabs と同じ方針) */
.theme--dark .tag-chip {
  color: rgba(255, 255, 255, 0.78);
  border-color: rgba(255, 255, 255, 0.22);
}
.theme--dark .tag-chip--selected {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.5);
  color: #fff;
}
.theme--dark .tag-chip:hover:not(.tag-chip--selected) {
  background: rgba(255, 255, 255, 0.08);
}
</style>
